// src/services/appointmentService.js

import apiClient from "./apiClient";
import {
  assertAppointmentCreated,
  normalizeCreateAppointmentPayload,
  normalizeAppointmentResponse,
} from "../utils/appointmentBookingUtils";
import {
  computeNextDailyTokenNumber,
  isTokenTakenByOther,
  resolveTokenDate,
} from "../utils/tokenSequenceUtils";
import { getOrganizationId } from "../utils/organizationUtils";

const API_BASE_URL = `/appointments`; // Use relative path since apiClient has baseURL

/** Serialize token generation in this tab so rapid registrations don't reuse max+1. */
let tokenGenerationChain = Promise.resolve();

/**
 * In-tab reservation of the last issued daily token per org+date.
 * Prevents reuse when getAllAppointments is briefly stale after a prior assign.
 */
const dailyTokenReservations = new Map();

const withTokenGenerationLock = (fn) => {
  const run = tokenGenerationChain.then(() => fn());
  // Keep the chain alive even if one generation fails
  tokenGenerationChain = run.catch(() => {});
  return run;
};

const parseAssignedToken = (payload) => {
  if (payload == null) return null;
  const candidates = [
    payload.tokenNumber,
    payload.token_number,
    payload.token,
    payload.number,
  ];
  for (const val of candidates) {
    if (val == null || val === "") continue;
    const n = Number(val);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const getDailyReservationKey = (tokenDate) => {
  let orgId = null;
  try {
    orgId = getOrganizationId();
  } catch (e) {
    orgId = null;
  }
  if (orgId == null) {
    try {
      const stored = localStorage.getItem("organizationId");
      if (stored) orgId = stored;
    } catch (e) {
      // ignore
    }
  }
  return `${orgId || "base"}:${tokenDate}`;
};

/** Next token = max(DB max for day, last reserved in this tab) + 1 */
const reserveNextDailyToken = (tokenDate, dbNext) => {
  const key = getDailyReservationKey(tokenDate);
  const reserved = dailyTokenReservations.get(key) || 0;
  const next = Math.max(Number(dbNext) || 1, reserved + 1);
  dailyTokenReservations.set(key, next);
  return next;
};

const releaseDailyTokenReservation = (tokenDate, tokenNumber) => {
  const key = getDailyReservationKey(tokenDate);
  const reserved = dailyTokenReservations.get(key);
  if (reserved === Number(tokenNumber)) {
    dailyTokenReservations.set(key, Math.max(0, reserved - 1));
  }
};

const collectTokenSourceRecords = async () => {
  // Cache-bust appointments so we never compute next token from a stale list.
  // Use appointments only — /tokens/all is not reliably org-scoped and can inflate
  // another hospital's daily max (e.g. org 5's #3 making org 4 start at #4).
  try {
    const response = await apiClient.get(API_BASE_URL, {
      params: { _ts: Date.now() },
    });
    if (Array.isArray(response.data)) return response.data;
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    }
    if (response.data && Array.isArray(response.data.appointments)) {
      return response.data.appointments;
    }
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  } catch (e) {
    return [];
  }
};

/** Sync in-tab reservation to the token that was actually assigned. */
const noteAssignedDailyToken = (tokenDate, tokenNumber) => {
  const n = Number(tokenNumber);
  if (!Number.isFinite(n) || n <= 0 || !tokenDate) return;
  const key = getDailyReservationKey(tokenDate);
  const prev = dailyTokenReservations.get(key) || 0;
  if (n > prev) dailyTokenReservations.set(key, n);
};
  
export const getAllAppointments = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    // If user has no organizationId, backend should use base table
    console.log(`[appointmentService] Fetching all appointments...`);
    const response = await apiClient.get(API_BASE_URL);
    console.log(`[appointmentService] getAllAppointments response status: ${response.status}`);
    console.log(`[appointmentService] getAllAppointments response data type: ${typeof response.data}`);
    console.log(`[appointmentService] getAllAppointments response data length: ${Array.isArray(response.data) ? response.data.length : 'not an array'}`);
    console.log(`[appointmentService] getAllAppointments response data:`, response.data);
    
    // Ensure response.data is an array
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && typeof response.data === 'object') {
      // Backend might return object with data array
      if (Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (Array.isArray(response.data.appointments)) {
        return response.data.appointments;
      }
      console.warn(`[appointmentService] getAllAppointments: Unexpected response format, returning empty array`);
      return [];
    }
    
    return response.data || [];
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn("[appointmentService] getAllAppointments: Organization-specific table not found, returning empty array:", error.response.data.message);
      return []; // Return empty array if table doesn't exist
    }
    console.error("[appointmentService] getAllAppointments: Error fetching appointments:", error);
    console.error("[appointmentService] getAllAppointments: Error response:", error.response?.data);
    // Return empty array instead of throwing to allow fallback to work
    return [];
  }
};

const stripTokenFieldsFromCreateBody = (body) => {
  const next = { ...body };
  delete next.skipToken;
  delete next.generateToken;
  delete next.autoGenerateToken;
  delete next.tokenNumber;
  delete next.token_number;
  delete next.token;
  delete next.tokenStatus;
  delete next.token_status;
  return next;
};

const clearTokenOnCreatedAppointment = async (created) => {
  if (!created || parseAssignedToken(created) == null) return created;
  const appointmentId = created.id || created.appointmentId;
  if (!appointmentId) return created;

  try {
    const updated = await updateAppointment(appointmentId, {
      patientId: Number(created.patientId ?? created.patient_id),
      doctorId: Number(created.doctorId ?? created.doctor_id),
      appointmentDateTime:
        created.appointmentDateTime ||
        created.appointment_date_time ||
        created.appointmentDate ||
        created.date,
      status: created.status || created.appointmentStatus || "PENDING_CONFIRMATION",
      paymentMethod:
        created.paymentMethod || created.payment_method || "PAY_AT_HOSPITAL",
      isCancel: created.isCancel === true || created.is_cancel === true,
      isVirtual: Boolean(created.isVirtual || created.is_virtual),
      tokenNumber: null,
    });
    return {
      ...created,
      ...(updated && typeof updated === "object" ? updated : {}),
      tokenNumber: null,
      token_number: null,
      token: null,
      tokenStatus: null,
      token_status: null,
    };
  } catch (error) {
    console.warn(
      "[appointmentService] Could not clear auto-assigned token after patient booking:",
      error
    );
    return created;
  }
};

export const createAppointment = async (appointment) => {
  try {
    const skipToken = Boolean(appointment?.skipToken);
    const payload = normalizeCreateAppointmentPayload(appointment);
    const { organizationId: payloadOrganizationId, ...appointmentBody } = payload;
    const orgId =
      payloadOrganizationId != null && Number(payloadOrganizationId) > 0
        ? Number(payloadOrganizationId)
        : getOrganizationId();
    const requestParams = {
      ...(orgId != null && orgId !== "" && Number(orgId) > 0
        ? { organizationId: Number(orgId) }
        : {}),
      ...(skipToken
        ? { generateToken: false, skipToken: true, autoGenerateToken: false }
        : {}),
    };
    const requestConfig =
      Object.keys(requestParams).length > 0
        ? { params: requestParams }
        : undefined;

    const response = await apiClient.post(
      `${API_BASE_URL}/create`,
      stripTokenFieldsFromCreateBody(appointmentBody),
      requestConfig
    );
    const created = assertAppointmentCreated(
      normalizeAppointmentResponse(response.data)
    );
    if (skipToken) {
      return await clearTokenOnCreatedAppointment(created);
    }
    return created;
  } catch (error) {
    console.error("[appointmentService] Error creating appointment:", error);
    console.error("[appointmentService] Error response:", error.response?.data);
    console.error("[appointmentService] Error status:", error.response?.status);
    
    // Provide more helpful error message
    if (error.response?.status === 500) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Internal server error";
      console.error(`[appointmentService] Backend error (500): ${errorMessage}`);
      
      // Check if it's a table-related error
      if (errorMessage.includes("table") || errorMessage.includes("Failed to") || errorMessage.includes("does not exist")) {
        throw new Error(`Appointment table not found or not accessible. Please ensure the organization-specific appointment table ({organizationId}_appointment) exists. Original error: ${errorMessage}`);
      }
      
      throw new Error(`Failed to create appointment: ${errorMessage}`);
    }
    
    throw error;
  }
};

/**
 * Patient portal booking.
 * Pay at hospital: save the visit with no token. Front desk confirmation
 * issues the next daily token through generateTokenForAppointment.
 * Pay online: issue that same daily token immediately after the visit is saved.
 */
export const createPatientPortalAppointment = async (appointment) => {
  const paymentMethod = String(appointment?.paymentMethod || "").toUpperCase();
  const payOnline = paymentMethod === "PAID_ONLINE" || paymentMethod === "ONLINE";
  const created = await createAppointment({
    ...appointment,
    skipToken: true,
  });

  if (!payOnline) return created;
  if (parseAssignedToken(created) != null) return created;

  const appointmentId = created?.id || created?.appointmentId;
  if (!appointmentId) return created;

  try {
    const tokenResult = await generateTokenForAppointment(appointmentId, {
      ...appointment,
      ...created,
      status: created?.status || appointment?.status || "CONFIRMED",
      paymentMethod: "PAID_ONLINE",
    });
    return {
      ...created,
      ...(tokenResult && typeof tokenResult === "object" ? tokenResult : {}),
    };
  } catch (error) {
    console.error(
      "[appointmentService] Online appointment was saved, but the daily token was not issued:",
      error
    );
    throw new Error(
      "Appointment confirmed, but the token could not be generated. Front desk can generate it from the appointments list."
    );
  }
};

export const updateAppointment = async (id, appointment) => {
  try {
    if (!id) {
      console.error("No ID provided for update.");
      throw new Error("Invalid appointment ID");
    }
    // Remove organizationId from appointment object if it exists (apiClient will add it as query param)
    const { organizationId, ...appointmentData } = appointment;
    
    // Ensure patientId is a number (not string) if it exists
    if (appointmentData.patientId != null) {
      appointmentData.patientId = Number(appointmentData.patientId);
    }
    
    // Ensure doctorId is a number (not string) if it exists
    if (appointmentData.doctorId != null) {
      appointmentData.doctorId = Number(appointmentData.doctorId);
    }

    // Persist prescription FK when doctor creates an OPD prescription
    if (appointmentData.prescriptionId != null && appointmentData.prescriptionId !== "") {
      const prescriptionId = Number(appointmentData.prescriptionId);
      if (Number.isFinite(prescriptionId) && prescriptionId > 0) {
        appointmentData.prescriptionId = prescriptionId;
        appointmentData.prescription_id = prescriptionId;
      }
    } else if (
      appointmentData.prescription_id != null &&
      appointmentData.prescription_id !== ""
    ) {
      const prescriptionId = Number(appointmentData.prescription_id);
      if (Number.isFinite(prescriptionId) && prescriptionId > 0) {
        appointmentData.prescriptionId = prescriptionId;
        appointmentData.prescription_id = prescriptionId;
      }
    }
    
    // Log the request for debugging
    const storedUser = localStorage.getItem("user");
    let orgId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        orgId = user?.organizationId;
      } catch (e) {
        // ignore
      }
    }
    console.log(`[appointmentService] updateAppointment: id=${id}, organizationId=${orgId}, data:`, appointmentData);
    
    // apiClient will automatically add organizationId as query parameter via interceptor
    // (not in body, as Appointment DTO doesn't accept it in request body)
    const response = await apiClient.put(`${API_BASE_URL}/${id}`, appointmentData);
    return response.data;
  } catch (error) {
    console.error("[appointmentService] Error updating appointment:", error);
    if (error.response) {
      console.error("[appointmentService] Server response:", error.response.data);
      console.error("[appointmentService] Status:", error.response.status);
      
      // Provide more helpful error message
      if (error.response.status === 500) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || "Internal server error";
        if (errorMessage.includes("not found") || errorMessage.includes("Appointment not found")) {
          throw new Error(`Appointment not found. The appointment with ID ${id} may not exist in the organization-specific appointments table, or the backend may be looking in the wrong table. Please ensure the appointment exists and the organizationId is correctly set.`);
        }
        throw new Error(`Failed to update appointment: ${errorMessage}`);
      }
    }
    throw error;
  }
};

export const updateTokenStatusToCalled = async (id) => {
  try {
    // apiClient will automatically add organizationId via interceptor (in request body for POST)
    // Also add it as query parameter as fallback in case backend expects it there
    console.log(`[appointmentService] updateTokenStatusToCalled: appointmentId=${id}`);
    
    // Get organizationId from localStorage to ensure it's available
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        organizationId = user?.organizationId || localStorage.getItem("organizationId");
      } catch (e) {
        console.error("[appointmentService] Error parsing user:", e);
      }
    }
    
    console.log(`[appointmentService] updateTokenStatusToCalled: organizationId=${organizationId}`);
    
    // apiClient will add organizationId to request body automatically via interceptor
    // Also add it as query parameter as some endpoints might expect it there
    const response = await apiClient.post(
      `${API_BASE_URL}/${id}/token-status/called`,
      {}, // Empty body, organizationId will be added by interceptor
      {
        params: organizationId ? { organizationId: organizationId } : {}
      }
    );
    
    console.log(`[appointmentService] updateTokenStatusToCalled: Success, response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[appointmentService] Error updating token status to called for appointment ${id}:`, error);
    console.error(`[appointmentService] Error response:`, error.response?.data);
    console.error(`[appointmentService] Error status:`, error.response?.status);
    
    // Provide more helpful error message
    if (error.response?.status === 500 || error.response?.status === 404) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Appointment not found";
      console.error(`[appointmentService] Backend error: ${errorMessage}`);
      
      // Check if it's a table-related error
      if (errorMessage.includes("not found") || errorMessage.includes("Appointment not found")) {
        const storedUser = localStorage.getItem("user");
        const user = storedUser ? JSON.parse(storedUser) : null;
        const orgId = user?.organizationId || localStorage.getItem("organizationId");
        throw new Error(`Appointment not found. The backend may be looking in the wrong table. Appointment ID: ${id}, Organization ID: ${orgId}. Please ensure the backend endpoint queries the correct organization-specific appointments table (e.g., ${orgId}_appointments). Original error: ${errorMessage}`);
      }
      
      throw new Error(`Failed to update token status: ${errorMessage}`);
    }
    
    throw error;
  }
};

export const deleteAppointment = async (id) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    await apiClient.delete(`${API_BASE_URL}/${id}`);
  } catch (error) {
    console.error("[appointmentService] Error deleting appointment:", error);

    const status = error?.response?.status;
    const message =
      error?.response?.data?.message ||
      (typeof error?.response?.data === "string"
        ? error.response.data
        : "");

    // For multi-tenant setups, a 404 can mean the appointment does not exist
    // in the current organization's table. In that case, treat delete as a
    // no-op success so the UI flow is not broken.
    if (status === 404) {
      console.warn(
        "[appointmentService] deleteAppointment: Appointment not found for this organization, treating as already deleted.",
        { id, status, message }
      );
      return;
    }

    throw error;
  }
};

export const getTodaysAppointmentsByDoctor = async (doctorId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(
      `${API_BASE_URL}/doctor/${doctorId}/today`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching today's appointments:", error);
    throw error;
  }
};

export const getAppointmentsByPatientId = async (patient_id) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    console.log(`[appointmentService] Fetching appointments for patient ID: ${patient_id}`);
    const response = await apiClient.get(`${API_BASE_URL}/patient/${patient_id}`);
    console.log(`[appointmentService] Response status: ${response.status}`);
    console.log(`[appointmentService] Response data:`, response.data);
    console.log(`[appointmentService] Response data type:`, typeof response.data);
    console.log(`[appointmentService] Response data is array:`, Array.isArray(response.data));
    console.log(`[appointmentService] Response data length:`, response.data?.length || 0);
    
    // Check if response is an array
    if (Array.isArray(response.data)) {
      console.log(`[appointmentService] ✅ Returning ${response.data.length} appointments`);
      return response.data;
    } else if (response.data && typeof response.data === 'object') {
      // If backend returns an object, try to extract array from it
      console.log(`[appointmentService] Response is object, checking for appointments array...`);
      if (Array.isArray(response.data.appointments)) {
        console.log(`[appointmentService] Found appointments array in response object`);
        return response.data.appointments;
      } else if (Array.isArray(response.data.data)) {
        console.log(`[appointmentService] Found data array in response object`);
        return response.data.data;
      }
    }
    
    console.warn(`[appointmentService] ⚠️ Unexpected response format, returning empty array`);
    return [];
  } catch (error) {
    console.error("[appointmentService] Error fetching appointments by patient ID:", error);
    console.error("[appointmentService] Error response:", error.response?.data);
    console.error("[appointmentService] Error status:", error.response?.status);
    
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn("[appointmentService] Organization-specific appointments table not found, returning empty array:", error.response.data.message);
      return []; // Return empty array if table doesn't exist
    }
    
    console.error("[appointmentService] Error fetching appointments:", error);
    // Return empty array instead of throwing to prevent breaking the UI
    return [];
  }
};

export const getAppointmentsGroupedByPatient = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    // If user has no organizationId, backend should use base table
    console.log(`[appointmentService] Fetching appointments grouped by patient...`);
    const response = await apiClient.get(`${API_BASE_URL}/grouped-by-patient`);
    let data = response.data;
    
    console.log(
      `[appointmentService] Response status: ${response.status}, patient groups: ${data ? Object.keys(data).length : 0}`
    );
    
    // Validate: Check if data is empty or contains only placeholder/default data
    if (!data || typeof data !== 'object') {
      console.warn(`[appointmentService] ⚠️ Invalid response data, trying fallback...`);
      // Fallback: Try fetching all appointments and group them on frontend
      return await getAppointmentsGroupedByPatientFallback();
    }
    
    // Check if data is empty object
    if (Object.keys(data).length === 0) {
      console.warn(`[appointmentService] ⚠️ Backend returned empty object, trying fallback...`);
      // Fallback: Try fetching all appointments and group them on frontend
      return await getAppointmentsGroupedByPatientFallback();
    }
    
    // Filter out invalid entries (empty arrays, null values, or placeholder data)
    // Backend should handle organization filtering via organizationId query param
    const filteredData = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      // Only include if it's a non-empty array with valid appointment objects
      if (Array.isArray(value) && value.length > 0) {
        // Check if at least one appointment has valid data (not all null/undefined)
        const hasValidAppointment = value.some(apt => 
          apt && apt.id != null && (apt.patientId != null || apt.patientName)
        );
        if (hasValidAppointment) {
          filteredData[key] = value;
        }
      }
    });
    
    console.log(`[appointmentService] ✅ Returning ${Object.keys(filteredData).length} patient groups`);
    return filteredData;
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn("Organization-specific table not found, trying fallback:", error.response.data.message);
      // Try fallback before giving up
      return await getAppointmentsGroupedByPatientFallback();
    }
    console.error("Error fetching appointments grouped by patient:", error);
    console.error("Error response:", error.response?.data);
    // Try fallback before returning empty object
    return await getAppointmentsGroupedByPatientFallback();
  }
};

// Fallback: fetch all appointments in one call and group them by patient on
// the frontend. There used to be a second fallback ("Method 2") here that
// fetched every patient, then looped a /appointments/patient/{id} call per
// patient (batches of 5) — an N+1 request storm across the whole org. It only
// ran when this single-call fallback also came back empty, which in practice
// means the org genuinely has zero appointments, so it was never recovering
// real data — just adding up to hundreds of extra round trips on an already-
// failing path. Removed; a genuinely empty result now just stays empty.
const getAppointmentsGroupedByPatientFallback = async () => {
  try {
    console.log(`[appointmentService] Using fallback: Fetching all appointments and grouping by patient...`);

    const allAppointments = await getAllAppointments();
    console.log(`[appointmentService] Fallback: Fetched ${allAppointments?.length || 0} appointments`);

    const grouped = {};
    if (Array.isArray(allAppointments)) {
      allAppointments.forEach(apt => {
        // Skip invalid appointments
        if (!apt || !apt.id) {
          return;
        }

        // Use patientId as key, or patientName if patientId is missing
        const key = apt.patientId != null ? String(apt.patientId) : (apt.patientName || 'unknown');

        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(apt);
      });
    }

    console.log(`[appointmentService] Fallback: Grouped into ${Object.keys(grouped).length} patient groups`);
    return grouped;
  } catch (fallbackError) {
    console.error("Error in fallback grouping:", fallbackError);
    return {}; // Return empty object if fallback also fails
  }
};

// New function to get appointments by doctor ID and date
export const getAppointmentsByDoctorAndDate = async (doctorId, date, organizationId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    console.log(`[appointmentService] getAppointmentsByDoctorAndDate: doctorId=${doctorId}, date=${date}`);
    const requestConfig =
      organizationId != null && organizationId !== ""
        ? { params: { organizationId: Number(organizationId) } }
        : undefined;
    const response = await apiClient.get(`${API_BASE_URL}/${doctorId}/${date}`, requestConfig);
    console.log(`[appointmentService] getAppointmentsByDoctorAndDate response status: ${response.status}`);
    console.log(`[appointmentService] getAppointmentsByDoctorAndDate response data:`, response.data);
    console.log(`[appointmentService] getAppointmentsByDoctorAndDate response data type:`, typeof response.data);
    console.log(`[appointmentService] getAppointmentsByDoctorAndDate response data is array:`, Array.isArray(response.data));
    
    const appointments = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
    console.log(`[appointmentService] getAppointmentsByDoctorAndDate returning ${appointments.length} appointments`);
    if (appointments.length > 0) {
      console.log(`[appointmentService] Sample appointment:`, appointments[0]);
    }
    return appointments;
  } catch (error) {
    console.error(`[appointmentService] Error fetching appointments by doctor ${doctorId} and date ${date}:`, error);
    console.error(`[appointmentService] Error response:`, error.response?.data);
    console.error(`[appointmentService] Error status:`, error.response?.status);
    
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn(`[appointmentService] Organization-specific appointments table not found, returning empty array`);
      return [];
    }
    
    // Return empty array instead of throwing to prevent breaking the UI
    return [];
  }
};

const generateTokens = async (doctorId) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(`${API_BASE_URL}/doctor/${doctorId}/appointments/tokenize`);
  if (!response.ok) throw new Error("Failed to generate tokens");
  return await response.json();
};

const buildTokenPreserveFields = (appointmentData, requestBody = {}) => {
  const preserve = appointmentData || {};
  const fields = {};
  const patientId =
    requestBody.patientId ?? preserve.patientId ?? preserve.patient_id;
  const doctorId =
    requestBody.doctorId ?? preserve.doctorId ?? preserve.doctor_id;
  const appointmentDateTime =
    requestBody.appointmentDateTime ||
    preserve.appointmentDateTime ||
    preserve.appointment_date_time ||
    preserve.appointmentDate ||
    preserve.date;
  const status = requestBody.status ?? preserve.status;
  const appointmentType =
    requestBody.appointmentType ||
    preserve.appointmentType ||
    preserve.appointment_type;

  if (patientId != null) fields.patientId = patientId;
  if (doctorId != null) fields.doctorId = doctorId;
  if (appointmentDateTime != null) fields.appointmentDateTime = appointmentDateTime;
  if (status != null) fields.status = status;
  if (appointmentType != null) fields.appointmentType = appointmentType;

  if (requestBody.isCancel !== undefined) {
    fields.isCancel = requestBody.isCancel;
  } else if (preserve.isCancel !== undefined) {
    fields.isCancel = preserve.isCancel;
  } else if (preserve.is_cancel !== undefined) {
    fields.isCancel = preserve.is_cancel;
  }
  return fields;
};

/**
 * After OPD prescription create, store prescription_id on the appointment.
 * Preserves existing appointment fields so PUT does not wipe token/status/datetime.
 * Failures are thrown to the caller; prescription create should treat this as non-fatal.
 */
export const linkPrescriptionToAppointment = async (
  appointmentId,
  prescriptionId,
  appointmentSnapshot = null
) => {
  const apptId = Number(appointmentId);
  const rxId = Number(prescriptionId);
  if (!Number.isFinite(apptId) || apptId <= 0) {
    return null;
  }
  if (!Number.isFinite(rxId) || rxId <= 0) {
    return null;
  }

  let snapshot =
    appointmentSnapshot && typeof appointmentSnapshot === "object"
      ? appointmentSnapshot
      : null;

  if (!snapshot) {
    try {
      const rows = await getAllAppointments();
      snapshot = (Array.isArray(rows) ? rows : []).find(
        (row) =>
          Number(row?.id) === apptId ||
          Number(row?.appointmentId) === apptId ||
          Number(row?.appointment_id) === apptId
      );
    } catch (lookupError) {
      console.warn(
        "[appointmentService] Could not load appointment before linking prescription:",
        lookupError
      );
      snapshot = null;
    }
  }

  const payload = {
    ...buildTokenPreserveFields(snapshot || {}, {}),
    prescriptionId: rxId,
    prescription_id: rxId,
  };

  const tokenNumber = snapshot?.tokenNumber ?? snapshot?.token_number;
  const tokenStatus = snapshot?.tokenStatus ?? snapshot?.token_status;
  if (tokenNumber != null && tokenNumber !== "") {
    payload.tokenNumber = tokenNumber;
    payload.token_number = tokenNumber;
  }
  if (tokenStatus) {
    payload.tokenStatus = tokenStatus;
    payload.token_status = tokenStatus;
  }

  console.log(
    `[appointmentService] linkPrescriptionToAppointment: appointmentId=${apptId}, prescriptionId=${rxId}`
  );
  return updateAppointment(apptId, payload);
};

const persistDailyTokenNumber = async (
  appointmentId,
  tokenNumber,
  tokenStatus,
  appointmentData,
  requestBody
) => {
  const correction = {
    ...buildTokenPreserveFields(appointmentData, requestBody),
    tokenNumber,
    token_number: tokenNumber,
    tokenStatus: tokenStatus || "WAITING",
    token_status: tokenStatus || "WAITING",
  };
  await updateAppointment(appointmentId, correction);
  return correction;
};

// New endpoint for updating token
export const generateTokenForAppointment = async (appointmentId, appointmentData = null) => {
  return withTokenGenerationLock(async () => {
    let reservedToken = null;
    let tokenDate = null;
    try {
      // apiClient will automatically add organizationId via interceptor
      console.log(`[appointmentService] Generating token for appointment ${appointmentId}`);

      // Prefer backend daily_token_counters (per organizationId + tokenDate).
      // Frontend suggestion is only a hint / fallback when backend returns nothing.
      tokenDate = resolveTokenDate(appointmentData);
      const sourceRecords = await collectTokenSourceRecords();
      const dbNext = computeNextDailyTokenNumber(sourceRecords, tokenDate, {
        excludeAppointmentId: appointmentId,
      });
      const suggestedToken = reserveNextDailyToken(tokenDate, dbNext);
      reservedToken = suggestedToken;

      // If appointment data is provided, send it in the request body to preserve all fields
      // This prevents the backend from setting fields to null during the update
      const requestBody = {
        tokenDate,
        // Hint only — backend atomic counter is authoritative when present
        suggestedTokenNumber: suggestedToken,
        tokenNumber: suggestedToken,
      };
      if (appointmentData) {
        // Include essential appointment fields to preserve them during token generation
        // The backend should use these fields to ensure no data is lost
        requestBody.patientId = appointmentData.patientId || appointmentData.patient_id;
        requestBody.doctorId = appointmentData.doctorId || appointmentData.doctor_id;
        const apptDT =
          appointmentData.appointmentDateTime ||
          appointmentData.appointment_date_time ||
          appointmentData.appointmentDate ||
          appointmentData.date ||
          null;
        requestBody.appointmentDateTime = apptDT;
        requestBody.status = appointmentData.status;
        requestBody.appointmentType =
          appointmentData.appointmentType || appointmentData.appointment_type;
        requestBody.isCancel =
          appointmentData.isCancel !== undefined
            ? appointmentData.isCancel
            : appointmentData.is_cancel !== undefined
              ? appointmentData.is_cancel
              : false;

        // Include other optional fields if they exist
        if (appointmentData.notes !== undefined) requestBody.notes = appointmentData.notes;
        if (appointmentData.locationName !== undefined) {
          requestBody.locationName =
            appointmentData.locationName || appointmentData.location_name;
        }
        if (appointmentData.locationRoom !== undefined) {
          requestBody.locationRoom =
            appointmentData.locationRoom || appointmentData.location_room;
        }
        if (appointmentData.virtualMeetingUrl !== undefined) {
          requestBody.virtualMeetingUrl =
            appointmentData.virtualMeetingUrl || appointmentData.virtual_meeting_url;
        }
        if (appointmentData.virtualMeetingInstructions !== undefined) {
          requestBody.virtualMeetingInstructions =
            appointmentData.virtualMeetingInstructions ||
            appointmentData.virtual_meeting_instructions;
        }
        if (appointmentData.paymentMethod !== undefined) {
          requestBody.paymentMethod =
            appointmentData.paymentMethod || appointmentData.payment_method;
        }

        console.log(
          `[appointmentService] Sending appointment data to preserve fields:`,
          requestBody
        );
      }

      const response = await apiClient.post(
        `${API_BASE_URL}/${appointmentId}/generate-token`,
        requestBody
      );
      console.log(`[appointmentService] Token generated successfully:`, response.data);

      const tokenStatus =
        response.data?.tokenStatus ||
        response.data?.token_status ||
        "WAITING";

      const backendAssigned = parseAssignedToken(response.data);

      // Backend daily counter is source of truth. Do NOT overwrite a valid backend
      // token with a frontend max+1 (that was mixing other orgs / stale lists and
      // turned org 4's #1 into #4 after org 5 had issued #1–#3).
      let assignedToken = backendAssigned;

      if (assignedToken == null) {
        // Backend did not return a number — fall back to frontend suggestion
        assignedToken = suggestedToken;
        try {
          await persistDailyTokenNumber(
            appointmentId,
            assignedToken,
            tokenStatus,
            appointmentData,
            requestBody
          );
          console.log(
            `[appointmentService] Backend omitted tokenNumber; persisted fallback #${assignedToken}`
          );
        } catch (persistErr) {
          console.warn(
            "[appointmentService] Could not persist fallback token via updateAppointment:",
            persistErr
          );
          throw persistErr;
        }
      } else if (assignedToken !== suggestedToken) {
        // Backend counter disagreed with frontend hint — trust backend, drop hint reservation
        releaseDailyTokenReservation(tokenDate, reservedToken);
        reservedToken = assignedToken;
        console.log(
          `[appointmentService] Using backend daily token #${assignedToken} (frontend had suggested #${suggestedToken})`
        );
      }

      // Only correct when the backend value is already used by another appointment today
      const latestRecords = await collectTokenSourceRecords();
      if (
        isTokenTakenByOther(latestRecords, tokenDate, assignedToken, appointmentId)
      ) {
        const conflictNext = computeNextDailyTokenNumber(latestRecords, tokenDate, {
          excludeAppointmentId: appointmentId,
        });
        const corrected = reserveNextDailyToken(tokenDate, conflictNext);
        reservedToken = corrected;
        try {
          await persistDailyTokenNumber(
            appointmentId,
            corrected,
            tokenStatus,
            appointmentData,
            requestBody
          );
          assignedToken = corrected;
          console.log(
            `[appointmentService] Backend token #${backendAssigned} conflicted; corrected to #${corrected}`
          );
        } catch (correctErr) {
          console.warn(
            "[appointmentService] Could not correct duplicate token via updateAppointment:",
            correctErr
          );
        }
      }

      noteAssignedDailyToken(tokenDate, assignedToken);

      return {
        ...(response.data || {}),
        tokenNumber: assignedToken,
        token_number: assignedToken,
        tokenStatus,
      };
    } catch (error) {
      if (tokenDate != null && reservedToken != null) {
        releaseDailyTokenReservation(tokenDate, reservedToken);
      }
      console.error("[appointmentService] Error generating token:", error);
      console.error("[appointmentService] Error response:", error.response?.data);
      console.error("[appointmentService] Error status:", error.response?.status);

      // Provide more helpful error message
      if (error.response?.status === 500 || error.response?.status === 404) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Appointment not found";
        console.error(`[appointmentService] Backend error: ${errorMessage}`);

        // Check if it's a table-related error
        if (
          errorMessage.includes("not found") ||
          errorMessage.includes("Appointment not found")
        ) {
          throw new Error(
            `Appointment not found. The backend may be looking in the wrong table. Please ensure the backend endpoint accepts organizationId and queries the correct organization-specific appointments table. Original error: ${errorMessage}`
          );
        }

        throw new Error(`Failed to generate token: ${errorMessage}`);
      }

      throw error;
    }
  });
};

export default {
  getAllAppointments,
  updateAppointment,
  deleteAppointment,
  getTodaysAppointmentsByDoctor,
  getAppointmentsByDoctorAndDate, 
  getAppointmentsByPatientId,
  generateTokens,
  updateTokenStatusToCalled,
  generateTokenForAppointment,
  getAppointmentsGroupedByPatient,
  linkPrescriptionToAppointment,
};
